/* eslint-disable no-magic-numbers */
import { defineMessages, IntlShape, FormatNumberOptions } from 'react-intl';

import {
  CURRENCY_DECIMAL_PLACES,
  DEFAULT_CURRENCY_SYMBOL,
  INTL_NUMBER_REGEX,
  LIMIT_FOR_RENDERING_BILLIONS,
  LIMIT_FOR_RENDERING_MILLIONS,
  LIMIT_FOR_RENDERING_THOUSANDS,
  LIMIT_FOR_RENDERING_TRILLIONS,
  OTHER_DOLLAR_SIGNS
} from './constants';
import { AbbreviationLocalisationKey } from './types';

export function truncateNumber(value: number, maxDigits: number): number {
  const asString = value.toString();
  const [digits, fractions] = asString.split('.');
  const digitsFree = maxDigits - digits.length;
  const fractionsLength = fractions ? fractions.length : 0;
  const maxFractions = Math.max(Math.min(fractionsLength, digitsFree), 0);
  return parseFloat(value.toFixed(maxFractions));
}

export const abbreviationDescriptors = defineMessages({
  [AbbreviationLocalisationKey.THOUSANDS]: {
    id: 'abbreviation.thousands',
    description: 'Abbreviation for Thousands',
    defaultMessage: 'K'
  },
  [AbbreviationLocalisationKey.MILLIONS]: {
    id: 'abbreviation.millions',
    description: 'Abbreviation for Millions',
    defaultMessage: 'M'
  },
  [AbbreviationLocalisationKey.BILLIONS]: {
    id: 'abbreviation.billions',
    description: 'Abbreviation for Billions',
    defaultMessage: 'B'
  },
  [AbbreviationLocalisationKey.TRILLIONS]: {
    id: 'abbreviation.trillions',
    description: 'Abbreviation for Trillions',
    defaultMessage: 'T'
  }
});

function shouldConstrain(value: number, constraint: number): boolean {
  if (value >= constraint || value <= -Math.abs(constraint)) {
    return true;
  }
  return false;
}

export function constrainNumberWithSuffix(
  value: number,
  maxDigits: number
): [number, AbbreviationLocalisationKey | undefined] {
  if (shouldConstrain(value, LIMIT_FOR_RENDERING_TRILLIONS)) {
    return [truncateNumber(value / LIMIT_FOR_RENDERING_TRILLIONS, maxDigits), AbbreviationLocalisationKey.TRILLIONS];
  }
  if (shouldConstrain(value, LIMIT_FOR_RENDERING_BILLIONS)) {
    return [truncateNumber(value / LIMIT_FOR_RENDERING_BILLIONS, maxDigits), AbbreviationLocalisationKey.BILLIONS];
  }
  if (shouldConstrain(value, LIMIT_FOR_RENDERING_MILLIONS)) {
    return [truncateNumber(value / LIMIT_FOR_RENDERING_MILLIONS, maxDigits), AbbreviationLocalisationKey.MILLIONS];
  }
  if (shouldConstrain(value, LIMIT_FOR_RENDERING_THOUSANDS)) {
    return [truncateNumber(value / LIMIT_FOR_RENDERING_THOUSANDS, maxDigits), AbbreviationLocalisationKey.THOUSANDS];
  }
  return [truncateNumber(value, maxDigits), undefined];
}

export const abbreviateNumber = (
  value: number,
  maxDigits: number,
  style: FormatNumberOptions['style'],
  currency: string,
  includeDecimals = false,
  intl: IntlShape
): string => {
  const maxDigitsNegativeAware = value > 0 ? maxDigits : maxDigits - 1;

  const valueWithoutDecimals = includeDecimals ? value : Math.trunc(value);

  const [constrainedValue, suffixKey] = constrainNumberWithSuffix(valueWithoutDecimals, maxDigitsNegativeAware);

  // If style="currency" intl formatNumber does not allow currency to be empty
  // Hence we need to just use double digit decimals as a default fallback in this case
  // This is because currency can be an  empty string as per our requirement
  const formatterConfig: FormatNumberOptions = {
    style,
    currency,
    maximumFractionDigits: CURRENCY_DECIMAL_PLACES,
    minimumFractionDigits: 0
  };

  if (style === 'currency' && !currency) {
    formatterConfig.style = 'decimal';
    delete formatterConfig.currency;
  }

  const formattedNumber = intl.formatNumber(constrainedValue, formatterConfig);
  const suffix = suffixKey ? intl.formatMessage(abbreviationDescriptors[suffixKey]) : '';

  return formattedNumber.replace(INTL_NUMBER_REGEX, (match: string) => `${match}${suffix}`);
};

export const formatPercentage = (value: number, intl: IntlShape): string =>
  intl.formatNumber(value, {
    style: 'percent'
  });

export const formatCurrency = (
  value: number,
  intl: IntlShape,
  currency?: string,
  minimumFractionDigits?: number,
  maximumFractionDigits?: number,
  useNarrowSymbol = true
): string => {
  // If style="currency" intl formatNumber does not allow currency to be empty
  // Hence we need to just use double digit decimals as a default fallback in this case
  // This is because currency can be an  empty string as per our requirement
  const formatterConfig: FormatNumberOptions = {
    currency,
    currencyDisplay: useNarrowSymbol ? 'narrowSymbol' : 'symbol',
    minimumFractionDigits,
    style: 'currency',
    maximumFractionDigits: maximumFractionDigits ?? CURRENCY_DECIMAL_PLACES
  };

  if (!currency) {
    formatterConfig.style = 'decimal';
    delete formatterConfig.currency;
  }

  return intl.formatNumber(value, formatterConfig).replace(OTHER_DOLLAR_SIGNS, '$');
};

export const formatDecimal = (
  value: number,
  intl: IntlShape,
  minimumFractionDigits?: number,
  maximumFractionDigits?: number
): string =>
  intl.formatNumber(value, {
    minimumFractionDigits,
    maximumFractionDigits,
    style: 'decimal'
  });

export const formatShortMonth = (value: string | number, intl: IntlShape): string =>
  intl.formatDate(value, {
    month: 'short'
  });

export const formatShortMonthYear = (value: string, intl: IntlShape): string =>
  intl.formatDate(value, {
    month: 'short',
    year: '2-digit'
  });

/**
 * Leverages Intl.NumberFormatter to resolve a currency code to a currency symbol.
 * If it fails, just return the default provided symbol.
 *
 * @example getCurrencySymbolFromCode('USD') -> '$'
 *
 * @param {string} code
 * @param {string} defaultSymbol
 * @returns {string}
 */
export const getCurrencySymbolFromCode = (code: string, defaultSymbol: string = DEFAULT_CURRENCY_SYMBOL): string => {
  let currencyPart: Intl.NumberFormatPart | undefined;

  try {
    const fmt = new Intl.NumberFormat('en', { style: 'currency', currency: code });
    currencyPart = fmt.formatToParts().find((part: Intl.NumberFormatPart) => part.type === 'currency');
  } catch (e) {
    /** noop */
  }

  if (!currencyPart) {
    return defaultSymbol;
  }

  return currencyPart.value;
};

// Helper function to add ordinal suffix
// TODO: should be localized
export const getOrdinalSuffix = (number: number, locale: string) => {
  const localLang = locale.split('-')[0];
  if (localLang === 'en') {
    const suffixes = ['th', 'st', 'nd', 'rd'];
    const remainder10 = number % 10;
    const remainder100 = number % 100;

    // Special cases for 11, 12, 13
    if (remainder100 >= 11 && remainder100 <= 13) {
      return `${number}th`;
    }

    // Default cases
    return `${number}${suffixes[remainder10] || 'th'}`;
  } else if (localLang === 'fr') {
    return number === 1 ? `${number}er` : `${number}e`;
  } else if (localLang === 'es') {
    return `${number}º`;
  } else {
    return `${number}`;
  }
};
